<!-- eslint-disable vue/no-v-html -->
<template>
  <UiModalContainer :modal-show="modal" @handleClick="handleModalAction">
    <template v-slot:header>
      <div class="flex flex-col md:flex-row md:items-center md:gap-4">
        <AuthorCard :author="authorName" :time="timeDisplay" />
        <div v-if="post?.labels?.length" class="flex flex-wrap gap-2 mt-2 md:mt-0 md:ml-1">
          <Chips
            v-for="label in post?.labels"
            :key="label.id"
            :title="label.title"
            child-class="bg-primary-purple-50 text-primary-purple-700 border-primary-purple-200"
          />
        </div>
      </div>
    </template>

    <div class="flex flex-col h-full">
      <div v-if="post" class="flex-1 flex flex-col min-h-0">
        <!-- Post preview -->
        <div class="border border-border rounded-lg p-3 bg-bg-color-white mb-4">
          <PostMarkdown :text="postText" />
          <PostMedia :media="postMedia" />

          <!-- Post actions -->
          <div class="flex items-center gap-4 mt-3 pt-3 border-t border-border">
            <UIButton
              :button="postLiked ? 'primary' : 'primary-button-outline'"
              @click="togglePostLike"
            >
              <span
                class="font-semibold"
                :class="postLiked ? 'text-white' : 'text-primary-purple-600'"
              >
                Like{{ postLikes ? ` (${postLikes})` : '' }}
              </span>
            </UIButton>
            <UIButton button="primary-button-outline" @click="focusCommentInput">
              <span class="font-semibold text-primary-purple-600">
                Comment{{ commentsCount ? ` (${commentsCount})` : '' }}
              </span>
            </UIButton>
          </div>
        </div>

        <!-- Comments heading -->
        <Heading
          :heading="`Comments (${commentsCount})`"
          :wrapper-class="'mb-4 mt-6'"
          :display-breadcrumb="false"
        />

        <!-- Comments thread - scrollable area -->
        <div class="flex-1 overflow-auto pr-1 pb-6">
          <div class="space-y-4">
            <div v-for="(comment, idx) in comments" :key="idx" class="flex gap-3">
              <div
                class="w-8 h-8 rounded-full bg-primary-purple-100 flex items-center justify-center flex-shrink-0 mt-1"
              >
                <icon class="w-4 h-4" icon="user" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="bg-gray-100 rounded-2xl px-3 py-2 max-w-fit">
                  <div class="text-sm font-semibold text-primary-gray-900 mb-1">
                    {{ comment?.inserted_by?.full_name }}
                  </div>
                  <div class="text-sm text-primary-gray-900 leading-relaxed">
                    {{ comment?.title }}
                  </div>
                </div>
                <div class="flex items-center gap-4 mt-1 ml-1">
                  <span class="text-xs text-primary-gray-500">
                    {{ displayPastTime(comment) }}
                  </span>
                  <button
                    class="text-xs font-semibold hover:underline"
                    :class="
                      comment?.liked_by_you ? 'text-primary-purple-600' : 'text-primary-gray-700'
                    "
                    @click="toggleCommentLike(comment)"
                  >
                    Like{{ comment?.likes_count ? ` (${comment.likes_count})` : '' }}
                  </button>
                  <button
                    class="text-xs font-semibold hover:underline text-primary-gray-700"
                    @click="toggleReply(idx)"
                  >
                    Reply
                  </button>
                </div>

                <!-- Reply input -->
                <div v-if="replyingTo === idx" class="mt-3 ml-1">
                  <div class="flex gap-2">
                    <div
                      class="w-6 h-6 rounded-full bg-primary-purple-100 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <icon class="w-3 h-3" icon="user" />
                    </div>
                    <div class="flex-1">
                      <UiInputBox
                        v-model="replyText"
                        type="textarea"
                        :rows="1"
                        :placeholder="`Reply to ${comment.inserted_by?.full_name}...`"
                        :hide-title="true"
                        :is-not-translated="true"
                        :no-error="true"
                      />
                      <div class="flex gap-2 mt-2">
                        <UIButton
                          :disabled="!replyText.trim()"
                          button="primary"
                          size="xs"
                          @click="submitReply(comment)"
                        >
                          Reply
                        </UIButton>
                        <UIButton button="primary-button-outline" size="xs" @click="cancelReply">
                          Cancel
                        </UIButton>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Replies display -->
                <div v-if="comment?.replies?.length" class="mt-3 ml-8 space-y-3">
                  <div
                    v-for="(reply, replyIdx) in comment.replies"
                    :key="replyIdx"
                    class="flex gap-2"
                  >
                    <div
                      class="w-6 h-6 rounded-full bg-primary-purple-100 flex items-center justify-center flex-shrink-0 mt-2"
                    >
                      <icon class="w-3 h-3" icon="user" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="bg-gray-50 rounded-2xl px-3 py-2 max-w-fit">
                        <div class="text-xs font-semibold text-primary-gray-900 mb-1">
                          {{ reply.inserted_by?.full_name }}
                        </div>
                        <div class="text-xs text-primary-gray-900 leading-relaxed">
                          {{ reply.title }}
                        </div>
                      </div>
                      <div class="flex items-center gap-3 mt-1 ml-1">
                        <span class="text-xs text-primary-gray-500">
                          {{ displayPastTime(reply) }}
                        </span>
                        <button
                          class="text-xs font-semibold hover:underline"
                          :class="
                            reply?.liked_by_you
                              ? 'text-primary-purple-600'
                              : 'text-primary-gray-700'
                          "
                          @click="toggleReplyLike(reply)"
                        >
                          Like{{ reply.likes_count ? ` (${reply.likes_count})` : '' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky comment input at bottom -->
        <div class="sticky bottom-0 bg-white border-t border-border pt-4 pb-4 px-4 -mx-4">
          <div class="w-full flex items-center gap-3">
            <div
              class="w-9 h-9 rounded-full bg-primary-purple-100 flex items-center justify-center"
            >
              <icon class="w-4 h-4" icon="user" />
            </div>
            <div class="flex-1">
              <UiInputBox
                ref="commentInput"
                v-model="newComment"
                type="textarea"
                :rows="2"
                placeholder="Write a comment..."
                :hide-title="true"
                :is-not-translated="true"
                :no-error="true"
              />
            </div>
            <div>
              <UIButton :disabled="!newComment.trim()" button="primary" @click="submitComment">
                Post
              </UIButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </UiModalContainer>
</template>

<script>
import icon from '@components/icons/icon.vue'
import UiModalContainer from '@src/components/UiElements/UiModalContainer.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import UiInputBox from '@src/components/UiElements/UiInputBox.vue'
import PostMarkdown from '@src/components/notice-board/PostMarkdown.vue'
import PostMedia from '@src/components/notice-board/PostMedia.vue'
import AuthorCard from '@src/components/notice-board/AuthorCard.vue'
import Heading from '@src/components/Heading.vue'
import { renderMarkdown } from '@src/lib/markdown'
import { mapActions } from 'vuex'
import formatDateRelative from '@src/utils/format-date-relative'
import Chips from '@src/components/UiElements/Chips.vue'
import { buildWhereQuery } from '@/src/utils/filters'
import FILTER_KEYS from '@src/constants/filter-constants.js'

export default {
  name: 'CommentModal',
  components: {
    icon,
    UiModalContainer,
    UIButton,
    UiInputBox,
    PostMarkdown,
    PostMedia,
    AuthorCard,
    Heading,
    Chips,
  },
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    post: {
      type: Object,
      default: null,
    },
  },
  emits: ['toggle', 'submitComment', 'togglePostLike'],
  data() {
    return {
      newComment: '',
      replyText: '',
      replyingTo: null,
      comments: [],
      isLoading: false,
    }
  },
  computed: {
    authorName() {
      return this.post?.inserted_by?.full_name || this.post?.author || ''
    },
    postLabels() {
      return this.post?.labels || []
    },
    timeDisplay() {
      const timestamp = this.post?.inserted_at || this.post?.time
      if (timestamp) return formatDateRelative(new Date(timestamp))
      return ''
    },
    postText() {
      return this.post?.description || this.post?.text || ''
    },
    postMedia() {
      return this.post?.media || null
    },
    postLiked() {
      return !!this.post?.liked_by_you
    },
    postLikes() {
      return this.post?.likes_count ?? this.post?.likes ?? 0
    },
    commentsCount() {
      if (!this.post) return 0
      return (
        this.post?.comments_count ??
        (Array.isArray(this.post?.comments) ? this.post.comments.length : 0)
      )
    },
  },
  watch: {
    post: {
      immediate: true,
      handler(newPost) {
        if (this.modal && newPost?.id) {
          this.filterComments()
        }
      },
    },
  },
  methods: {
    rendered(text) {
      return renderMarkdown(text)
    },
    handleModalAction(action) {
      if (action === 'close' || action === 'cancel') {
        this.$emit('toggle', action)
      }
    },
    displayPastTime(commentOrReply) {
      const timestamp = commentOrReply?.inserted_at || commentOrReply?.time
      if (timestamp) return formatDateRelative(new Date(timestamp))
      return ''
    },
    submitComment() {
      if (!this.newComment.trim() || !this.post?.id) return
      const payload = {
        title: this.newComment.trim(),
        post_id: this.post.id,
      }
      this.createComment({ comment: payload }).then(() => {
        this.$emit('submitComment')
        this.filterComments()
        this.newComment = ''
      })
    },
    filterComments() {
      const payload = {
        $where: buildWhereQuery(FILTER_KEYS.EQUAL, 'post_id', this.post.id),
      }
      this.isLoading = true
      this.getComments(payload)
        .then((res) => {
          this.comments = res?.data?.records || []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    toggleCommentLike(comment) {
      if (!comment?.id) return
      this.likePostOrCommentOrReply({
        comment_id: comment.id,
        emoji: 'like',
      }).then((res) => {
        const isUnliked = res?.data?.message?.toLowerCase().includes('unliked')
        comment.liked_by_you = !isUnliked
        comment.likes_count = isUnliked
          ? Math.max(0, (comment.likes_count || 0) - 1)
          : (comment.likes_count || 0) + 1
      })
    },
    toggleReply(index) {
      if (this.replyingTo === index) {
        this.cancelReply()
      } else {
        this.replyingTo = index
        this.replyText = ''
      }
    },
    cancelReply() {
      this.replyingTo = null
      this.replyText = ''
    },
    submitReply(comment) {
      if (!this.replyText.trim()) return
      if (!comment?.id) return
      const payload = {
        title: this.replyText.trim(),
        comment_id: comment.id,
      }
      this.createReply(payload).then(() => {
        this.filterComments()
        this.cancelReply()
      })
    },
    toggleReplyLike(reply) {
      if (!reply?.id) return

      this.likePostOrCommentOrReply({
        reply_id: reply.id,
        emoji: 'like',
      }).then((res) => {
        const isUnliked = res?.data?.message?.toLowerCase().includes('unliked')

        reply.liked_by_you = !isUnliked
        reply.likes_count = isUnliked
          ? Math.max(0, (reply.likes_count || 0) - 1)
          : (reply.likes_count || 0) + 1
      })
    },
    togglePostLike() {
      this.$emit('togglePostLike')
    },
    focusCommentInput() {
      // Focus the comment input in the sticky section
      this.$nextTick(() => {
        if (this.$refs.commentInput && this.$refs.commentInput.$el) {
          const textarea = this.$refs.commentInput.$el.querySelector('textarea')
          if (textarea) {
            textarea.focus()
          }
        }
      })
    },
    ...mapActions('noticeboard', [
      'createComment',
      'getComments',
      'likePostOrCommentOrReply',
      'createReply',
    ]),
  },
}
</script>
